import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { 
  ArrowRight, 
  ShoppingBag, 
  Package, 
  Leaf, 
  Crown, 
  Zap,
  Users,
  Globe,
  TrendingUp,
  Star,
  Heart,
  Award,
  ShoppingCart,
  Eye,
  Truck
} from "lucide-react";

const EcommercePage = () => {
  const fadeInUp = {
    initial: { opacity: 0, y: 60 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.8, ease: "easeOut" }
  };

  const staggerContainer = {
    initial: {},
    animate: { transition: { staggerChildren: 0.1 } }
  };

  const services = [
    {
      icon: <Leaf className="w-8 h-8" />,
      title: "Herbal Wellness Products",
      description: "Premium natural supplements, teas, and wellness solutions that enhance physical and mental performance."
    },
    {
      icon: <Crown className="w-8 h-8" />,
      title: "Luxury Lifestyle Brands",
      description: "High-end streetwear, accessories, and lifestyle products that embody the Nhalege philosophy and aesthetic."
    },
    {
      icon: <Zap className="w-8 h-8" />,
      title: "Digital Products",
      description: "Exclusive digital downloads, templates, and tools that help entrepreneurs build their own empires."
    },
    {
      icon: <Package className="w-8 h-8" />,
      title: "Creator Partnerships",
      description: "Strategic collaborations with influencers and creators to develop and promote revolutionary product lines."
    }
  ];

  const metrics = [
    { number: "$4.2M", label: "Annual Revenue" },
    { number: "67K", label: "Happy Customers" },
    { number: "89%", label: "Profit Margin" },
    { number: "23", label: "Product Lines" }
  ];

  const products = [
    {
      title: "Empire Builder Tea Blend",
      category: "Wellness",
      price: "$47",
      rating: "4.9",
      sales: "2,847",
      image: "https://images.unsplash.com/photo-1589666564459-93cdd3ab856a",
      description: "Premium herbal blend designed to enhance focus and mental clarity for entrepreneurs"
    },
    {
      title: "Nhalege Legacy Hoodie",
      category: "Apparel",
      price: "$127",
      rating: "4.8",
      sales: "1,234",
      image: "https://images.pexels.com/photos/6809871/pexels-photo-6809871.jpeg",
      description: "Luxury streetwear that embodies the philosophy of building generational wealth"
    },
    {
      title: "Wealth Building Masterclass Bundle",
      category: "Digital",
      price: "$297",
      rating: "4.9",
      sales: "5,691",
      image: "https://images.pexels.com/photos/8108531/pexels-photo-8108531.jpeg",
      description: "Complete digital course package for financial freedom and empire building"
    }
  ];

  const brands = [
    {
      name: "Nhalege Naturals",
      focus: "Herbal Wellness",
      products: "47",
      revenue: "$1.8M",
      growth: "+156%",
      description: "Premium natural supplements and wellness products"
    },
    {
      name: "Empire Apparel",
      focus: "Luxury Streetwear",
      products: "23",
      revenue: "$1.2M",
      growth: "+89%",
      description: "High-end lifestyle clothing and accessories"
    },
    {
      name: "Wealth Builders Academy",
      focus: "Digital Education",
      products: "15",
      revenue: "$900K",
      growth: "+234%",
      description: "Digital courses and entrepreneurship tools"
    },
    {
      name: "Creator Collective",
      focus: "Partnership Brands",
      products: "31",
      revenue: "$700K",
      growth: "+67%",
      description: "Collaborative products with top creators"
    }
  ];

  return (
    <div className="bg-black text-white min-h-screen pt-20">
      {/* Hero Section */}
      <section className="relative py-24 overflow-hidden">
        {/* Sacred Geometry Background */}
        <div className="absolute inset-0 opacity-8">
          <motion.div 
            className="absolute inset-0"
            animate={{ rotate: 360 }}
            transition={{ duration: 150, repeat: Infinity, ease: "linear" }}
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='180' height='180' viewBox='0 0 180 180' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' stroke='%23f59e0b' stroke-opacity='0.1' stroke-width='1'%3E%3Ccircle cx='90' cy='90' r='70'/%3E%3Ccircle cx='90' cy='90' r='50'/%3E%3Ccircle cx='90' cy='90' r='30'/%3E%3Cpath d='M90 20v140M20 90h140'/%3E%3C/g%3E%3C/svg%3E")`,
              backgroundSize: '180px 180px'
            }}
          />
        </div>

        {/* Background Image */}
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1589666564459-93cdd3ab856a"
            alt="E-commerce and products"
            className="w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-black/80 via-black/60 to-purple-900/40"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center"
            initial="initial"
            animate="animate"
            variants={staggerContainer}
          >
            <motion.div 
              className="flex justify-center mb-8"
              variants={fadeInUp}
            >
              <div className="w-24 h-24 bg-gradient-to-br from-purple-400 to-pink-500 rounded-full flex items-center justify-center shadow-2xl shadow-purple-400/50">
                <ShoppingBag className="w-12 h-12 text-black" />
              </div>
            </motion.div>

            <motion.h1 
              className="text-6xl md:text-8xl font-bold mb-6"
              variants={fadeInUp}
            >
              Nhalege <span className="text-purple-400">E-Commerce</span>
            </motion.h1>
            
            <motion.p 
              className="text-2xl md:text-3xl text-purple-400 mb-8 font-light"
              variants={fadeInUp}
            >
              Influence and Income
            </motion.p>
            
            <motion.p 
              className="text-xl text-white/80 max-w-4xl mx-auto mb-12 leading-relaxed"
              variants={fadeInUp}
            >
              Revolutionary e-commerce empire that transforms ideas into profitable brands, 
              influences culture through strategic product development, and generates sustainable 
              income streams across multiple verticals.
            </motion.p>

            <motion.div 
              className="flex flex-col sm:flex-row gap-6 justify-center"
              variants={fadeInUp}
            >
              <button className="group bg-gradient-to-r from-purple-400 to-pink-500 text-black px-12 py-4 text-lg font-bold rounded-full hover:shadow-2xl hover:shadow-purple-400/40 transition-all duration-300 hover:scale-105">
                <div className="flex items-center space-x-2">
                  <span>Shop Now</span>
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </div>
              </button>
              
              <button className="group border-2 border-purple-400 text-purple-400 px-12 py-4 text-lg font-bold rounded-full hover:bg-purple-400 hover:text-black transition-all duration-300 hover:scale-105">
                <div className="flex items-center space-x-2">
                  <Users className="w-5 h-5" />
                  <span>Start Your Brand</span>
                </div>
              </button>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Business Metrics Section */}
      <section className="py-16 bg-gradient-to-r from-gray-900 to-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="grid grid-cols-2 md:grid-cols-4 gap-8"
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            {metrics.map((metric, index) => (
              <motion.div 
                key={metric.label}
                className="text-center"
                variants={fadeInUp}
              >
                <div className="text-4xl md:text-5xl font-bold text-purple-400 mb-2">
                  {metric.number}
                </div>
                <div className="text-white/70 text-sm font-medium tracking-wider uppercase">
                  {metric.label}
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Core Mission Section */}
      <section className="py-24 bg-black relative">
        <div className="absolute inset-0 opacity-5">
          <img 
            src="https://images.pexels.com/photos/6809871/pexels-photo-6809871.jpeg"
            alt="E-commerce products"
            className="w-full h-full object-cover"
          />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <motion.div 
            className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center"
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            <motion.div variants={fadeInUp}>
              <h2 className="text-5xl md:text-6xl font-bold mb-8">
                Core <span className="text-purple-400">Mission</span>
              </h2>
              <div className="space-y-6 text-lg text-white/80 leading-relaxed">
                <p>
                  Nhalege E-Commerce operates as the cultural influence arm of our empire, 
                  creating products that embody our philosophy while generating substantial revenue.
                </p>
                <p>
                  We reject mass-market mediocrity and instead develop premium products that 
                  align with our values of health, wealth, and spiritual/mental advancement.
                </p>
                <p>
                  Our approach focuses on <span className="text-purple-400 font-semibold">
                  premium product development, strategic creator partnerships, and cultural influence</span> 
                  to build brands that generate both profit and positive impact.
                </p>
              </div>
              
              <div className="mt-10 p-8 bg-gradient-to-br from-purple-400/10 to-transparent border border-purple-400/20 rounded-2xl">
                <h3 className="text-2xl font-bold text-purple-400 mb-4">E-Commerce Philosophy</h3>
                <ul className="space-y-3 text-white/80">
                  <li className="flex items-center space-x-3">
                    <div className="w-2 h-2 bg-purple-400 rounded-full"></div>
                    <span>Premium quality over quantity approach</span>
                  </li>
                  <li className="flex items-center space-x-3">
                    <div className="w-2 h-2 bg-purple-400 rounded-full"></div>
                    <span>Values-aligned product development</span>
                  </li>
                  <li className="flex items-center space-x-3">
                    <div className="w-2 h-2 bg-purple-400 rounded-full"></div>
                    <span>Strategic creator and influencer partnerships</span>
                  </li>
                  <li className="flex items-center space-x-3">
                    <div className="w-2 h-2 bg-purple-400 rounded-full"></div>
                    <span>Cultural influence through conscious products</span>
                  </li>
                </ul>
              </div>
            </motion.div>

            <motion.div 
              className="relative"
              variants={fadeInUp}
            >
              <div className="relative">
                <img 
                  src="https://images.pexels.com/photos/8108531/pexels-photo-8108531.jpeg"
                  alt="Product development"
                  className="w-full h-96 object-cover rounded-3xl shadow-2xl"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent rounded-3xl"></div>
                
                {/* Floating Revenue Card */}
                <div className="absolute -bottom-6 -right-6 bg-gradient-to-br from-gray-900 to-black border border-purple-400/30 rounded-2xl p-6 backdrop-blur-lg">
                  <div className="flex items-center space-x-4">
                    <TrendingUp className="w-8 h-8 text-purple-400" />
                    <div>
                      <div className="text-2xl font-bold text-white">$4.2M</div>
                      <div className="text-sm text-white/60">Annual Revenue</div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-24 bg-gradient-to-br from-gray-900 to-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center mb-16"
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <h2 className="text-5xl md:text-6xl font-bold mb-8">
              Product <span className="text-purple-400">Categories</span>
            </h2>
            <p className="text-xl text-white/80 max-w-3xl mx-auto">
              Diverse portfolio of premium products that generate wealth and influence culture
            </p>
          </motion.div>

          <motion.div 
            className="grid grid-cols-1 md:grid-cols-2 gap-8"
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            {services.map((service, index) => (
              <motion.div
                key={service.title}
                className="group bg-gradient-to-br from-gray-900/50 to-black/50 backdrop-blur-lg border border-white/10 rounded-2xl p-8 hover:border-purple-400/50 transition-all duration-500"
                variants={fadeInUp}
                whileHover={{ y: -5 }}
              >
                <div className="flex items-start space-x-6">
                  <div className="flex-shrink-0">
                    <div className="w-16 h-16 bg-gradient-to-br from-purple-400 to-pink-500 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                      <span className="text-black">{service.icon}</span>
                    </div>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-white mb-4 group-hover:text-purple-400 transition-colors duration-300">
                      {service.title}
                    </h3>
                    <p className="text-white/70 leading-relaxed">
                      {service.description}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Featured Products Section */}
      <section className="py-24 bg-black relative">
        <div className="absolute inset-0 opacity-10">
          <motion.div 
            animate={{ y: [0, -30, 0] }}
            transition={{ duration: 25, repeat: Infinity, ease: "linear" }}
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' stroke='%23f59e0b' stroke-opacity='0.1' stroke-width='1'%3E%3Ccircle cx='50' cy='50' r='40'/%3E%3Ccircle cx='50' cy='50' r='25'/%3E%3Ccircle cx='50' cy='50' r='10'/%3E%3C/g%3E%3C/svg%3E")`,
              backgroundSize: '100px 100px'
            }}
          />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <motion.div 
            className="text-center mb-16"
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <h2 className="text-5xl md:text-6xl font-bold mb-8">
              Featured <span className="text-purple-400">Products</span>
            </h2>
            <p className="text-xl text-white/80 max-w-3xl mx-auto">
              Premium products that embody the Nhalege philosophy and generate substantial revenue
            </p>
          </motion.div>

          <motion.div 
            className="grid grid-cols-1 md:grid-cols-3 gap-8"
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            {products.map((product, index) => (
              <motion.div
                key={product.title}
                className="group bg-gradient-to-br from-gray-900/80 to-black/80 backdrop-blur-lg border border-purple-400/20 rounded-3xl overflow-hidden hover:border-purple-400/50 transition-all duration-500"
                variants={fadeInUp}
                whileHover={{ y: -5, scale: 1.02 }}
              >
                <div className="aspect-square relative">
                  <img 
                    src={product.image}
                    alt={product.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent"></div>
                  <div className="absolute top-4 right-4">
                    <div className="bg-purple-400 text-black px-3 py-1 rounded-full text-sm font-bold flex items-center space-x-1">
                      <Star className="w-3 h-3" />
                      <span>{product.rating}</span>
                    </div>
                  </div>
                  <div className="absolute top-4 left-4">
                    <div className="bg-black/60 backdrop-blur-lg text-white px-3 py-1 rounded-full text-sm">
                      {product.category}
                    </div>
                  </div>
                </div>
                
                <div className="p-8">
                  <h3 className="text-2xl font-bold text-white mb-2 group-hover:text-purple-400 transition-colors duration-300">
                    {product.title}
                  </h3>
                  
                  <p className="text-white/70 mb-6 leading-relaxed">
                    {product.description}
                  </p>
                  
                  <div className="flex items-center justify-between mb-6 text-sm">
                    <div className="flex items-center space-x-4">
                      <div className="flex items-center space-x-1">
                        <ShoppingCart className="w-4 h-4 text-purple-400" />
                        <span className="text-white/60">{product.sales} sold</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <Heart className="w-4 h-4 text-purple-400" />
                        <span className="text-white/60">{product.rating} rating</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div className="text-3xl font-bold text-purple-400">{product.price}</div>
                    <button className="bg-gradient-to-r from-purple-400 to-pink-500 text-black px-6 py-2 font-bold rounded-full hover:shadow-lg hover:shadow-purple-400/30 transition-all duration-300 hover:scale-105 flex items-center space-x-1">
                      <ShoppingCart className="w-4 h-4" />
                      <span>Buy Now</span>
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Brand Portfolio Section */}
      <section className="py-24 bg-gradient-to-br from-gray-900 to-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center mb-16"
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <h2 className="text-5xl md:text-6xl font-bold mb-8">
              Brand <span className="text-purple-400">Portfolio</span>
            </h2>
            <p className="text-xl text-white/80 max-w-3xl mx-auto">
              Multiple revenue streams across diverse product categories and markets
            </p>
          </motion.div>

          <motion.div 
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8"
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            {brands.map((brand, index) => (
              <motion.div
                key={brand.name}
                className="group bg-gradient-to-br from-gray-900/50 to-black/50 backdrop-blur-lg border border-purple-400/20 rounded-2xl p-6 hover:border-purple-400/50 transition-all duration-500"
                variants={fadeInUp}
                whileHover={{ y: -5 }}
              >
                <div className="text-center">
                  <h3 className="text-xl font-bold text-white mb-2 group-hover:text-purple-400 transition-colors duration-300">
                    {brand.name}
                  </h3>
                  <div className="text-purple-400 font-semibold mb-4">{brand.focus}</div>
                  <p className="text-white/60 text-sm mb-6">{brand.description}</p>
                  
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-white/60">Products:</span>
                      <span className="text-white">{brand.products}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-white/60">Revenue:</span>
                      <span className="text-purple-400 font-bold">{brand.revenue}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-white/60">Growth:</span>
                      <span className="text-green-400 font-bold">{brand.growth}</span>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-24 bg-gradient-to-br from-purple-400/10 via-black to-black">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            <motion.h2 
              className="text-5xl md:text-6xl font-bold mb-8"
              variants={fadeInUp}
            >
              Build Your <span className="text-purple-400">Empire</span>
            </motion.h2>
            
            <motion.p 
              className="text-xl text-white/80 mb-12"
              variants={fadeInUp}
            >
              Ready to launch your own revolutionary brand? Partner with the e-commerce empire that turns ideas into profitable reality.
            </motion.p>
            
            <motion.div 
              className="grid grid-cols-1 md:grid-cols-2 gap-8"
              variants={fadeInUp}
            >
              <div className="bg-gradient-to-br from-gray-900/50 to-black/50 backdrop-blur-lg border border-purple-400/20 rounded-2xl p-8">
                <h3 className="text-2xl font-bold text-purple-400 mb-6">Start Your Brand</h3>
                <div className="space-y-4">
                  <input 
                    type="text" 
                    placeholder="Brand Concept"
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:border-purple-400"
                  />
                  <input 
                    type="text" 
                    placeholder="Target Market"
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:border-purple-400"
                  />
                  <select className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:border-purple-400">
                    <option value="">Product Category</option>
                    <option value="wellness">Wellness Products</option>
                    <option value="apparel">Apparel & Accessories</option>
                    <option value="digital">Digital Products</option>
                    <option value="lifestyle">Lifestyle Brands</option>
                  </select>
                  <button className="w-full bg-gradient-to-r from-purple-400 to-pink-500 text-black py-3 font-bold rounded-full hover:shadow-lg hover:shadow-purple-400/30 transition-all duration-300 hover:scale-105">
                    Launch Partnership
                  </button>
                </div>
              </div>
              
              <div className="bg-gradient-to-br from-gray-900/50 to-black/50 backdrop-blur-lg border border-purple-400/20 rounded-2xl p-8">
                <h3 className="text-2xl font-bold text-purple-400 mb-6">Shop Empire</h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 bg-white/5 rounded-lg">
                    <span className="text-white">Nhalege Naturals</span>
                    <button className="text-purple-400 hover:text-white transition-colors">Shop →</button>
                  </div>
                  <div className="flex items-center justify-between p-4 bg-white/5 rounded-lg">
                    <span className="text-white">Empire Apparel</span>
                    <button className="text-purple-400 hover:text-white transition-colors">Shop →</button>
                  </div>
                  <div className="flex items-center justify-between p-4 bg-white/5 rounded-lg">
                    <span className="text-white">Wealth Builders Academy</span>
                    <button className="text-purple-400 hover:text-white transition-colors">Shop →</button>
                  </div>
                  <button className="w-full bg-gradient-to-r from-purple-400 to-pink-500 text-black py-3 font-bold rounded-full hover:shadow-lg hover:shadow-purple-400/30 transition-all duration-300 hover:scale-105">
                    View All Products
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Navigation Footer */}
      <section className="py-16 bg-gray-900 border-t border-purple-400/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <Link 
              to="/real-estate"
              className="group flex items-center space-x-2 text-white/70 hover:text-purple-400 transition-colors duration-300 mb-4 md:mb-0"
            >
              <ArrowRight className="w-5 h-5 rotate-180 group-hover:-translate-x-1 transition-transform" />
              <span>Previous: Real Estate</span>
            </Link>
            
            <Link 
              to="/"
              className="text-purple-400 font-semibold hover:text-white transition-colors duration-300"
            >
              ⬟ Empire Overview
            </Link>
            
            <div className="group flex items-center space-x-2 text-white/40 mt-4 md:mt-0">
              <span>Empire Complete</span>
              <Award className="w-5 h-5" />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default EcommercePage;